import { getRequirementsTree } from '@bsa/scouting-js-utils/src/advancements';
import { NOT_STARTED } from '@bsa/scouting-js-utils/src/constants/advancements';
import { addMeritBadgeRequirementsStatus } from '@bsa/scouting-js-utils/src/merit-badges';
import { updateApprovedStatusFromChildren } from '@bsa/scouting-js-utils/src/requirements';
import addSSElectiveRequirementsStatus from '@bsa/scouting-js-utils/src/ss-electives';
import { isEmpty, isNil, uniqBy } from 'lodash';
import { forkJoin, of } from 'rxjs';
import { Observable } from 'rxjs/Observable';
import { zip } from 'rxjs/observable/zip';
import { catchError, map, switchMap } from 'rxjs/operators';
import stable from 'stable';

import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import { esbApi } from '@modules/rtk/esb-api';
import {
  advancementStatus,
  advancementStatusDate,
  filterRelationships,
  isValueTruthy,
  sentryReport,
} from '@modules/shared/utils';
import {
  ProgramId,
  advancementTypes,
  historyItemsTypes,
  venturingCoreAwardsIds,
} from '@shared/constants';
import { getAdvancementTypeTranslation } from '@shared/localization';
import { unitsAvailableAdvancements } from '@unit/constants';
import { esbApiService } from '@utils';

import { GET_YOUTH_REQUIREMENTS_AFTER_EDIT_ERROR } from '../constants';
import { getAdvancementUid } from '../utils';
import { editAdvRqmtsRequest } from './actionsTyped';

const ADVANCEMENTS_URI = '/advancements/v2';
const ADVANCEMENTS_URI_OLD = '/advancements';

const savePicture$ = (personGuid, oldPicture, newPicture) => {
  if (oldPicture === newPicture) {
    return Observable.of(oldPicture);
  }

  const method = oldPicture ? esbApiService.put$ : esbApiService.post$;

  return method(
    `/persons/${personGuid}/profilePicture`,
    {
      picture: newPicture,
    },
    {
      gtm: {
        label: '/persons/{personGuid}/profilePicture',
      },
    },
  ).mapTo(newPicture);
};

const saveProfilePicture$ = ({ personGuid, oldPicture, newPicture }) =>
  zip(savePicture$(personGuid, oldPicture, newPicture));

/**
 * @esbEndpoint GET /persons/v2/:userId/personprofile
 */
const getYouthProfile$ = userId =>
  esbApiService.get$(`/persons/v2/${userId}/personprofile`, {
    gtm: {
      label: '/persons/v2/{userId}/personprofile',
    },
  });

/**
 * @esbEndpoint PUT /persons/v2/:personGuid/profile
 */
const saveProfile$ = (data, personGuid) =>
  esbApiService.put$(`/persons/v2/${personGuid}/profile`, data, {
    gtm: {
      label: '/persons/v2/{personGuid}/profile',
    },
  });

/**
 * @esbEndpoint GET /persons/v2/:userId/relationships
 */
const getRelationships$ = userId =>
  esbApiService.get$(`/persons/v2/${userId}/relationships`, {
    gtm: {
      label: '/persons/v2/{userId}/relationships',
    },
  });

/**
 * @esbEndpoint PUT /advancements/users/:userId
 */
const updateSBProfile$ = (data, userId) =>
  esbApiService.put$(`/advancements/users/${userId}`, data, {
    gtm: {
      label: '/advancements/users/{userId}',
    },
  });

/**
 * @esbEndpoint POST /persons/v2/:personGuid/deleteProfileInfo
 */
const deleteProfileItem$ = (data, personGuid) =>
  esbApiService.post$(`/persons/v2/${personGuid}/deleteProfileInfo`, data, {
    gtm: {
      label: '/persons/v2/{personGuid}/deleteProfileInfo',
    },
  });

const getEligibleAdvancementTypes = (eligibleAdvancementTypes, unitTypeId) => {
  if (![ProgramId.VENTURING, ProgramId.SEA_SCOUT].includes(unitTypeId)) {
    return eligibleAdvancementTypes;
  }
  return [
    ...eligibleAdvancementTypes,
    ...unitsAvailableAdvancements[ProgramId.BOY_SCOUT],
  ];
};

const fallBackFactory =
  ({ eligibleAdvancementTypes, userId, unitTypeId }) =>
  (getAdvancements$, type) => {
    if (!eligibleAdvancementTypes.includes(type)) {
      return of([]);
    }
    return getAdvancements$(userId, unitTypeId).pipe(
      map(advancements =>
        advancements.map(adv => ({
          ...adv,
          type: historyItemsTypes.ADVANCEMENT,
        })),
      ),
    );
  };

/**
 * @esbEndpoint POST /advancements/youth/:userId/meritBadges
 */
const startYouthMeritBadge$ = ({
  userId,
  organizationGuid,
  id,
  versionId,
  dateStarted,
}) =>
  esbApiService.post$(
    `${ADVANCEMENTS_URI_OLD}/youth/${userId}/meritBadges`,
    {
      organizationGuid: organizationGuid,
      id: id,
      versionId: versionId,
      dateStarted: dateStarted,
    },
    {
      gtm: {
        label: `${ADVANCEMENTS_URI_OLD}/youth/{userId}/meritBadges`,
      },
    },
  );

const dedupeAdvancements = advs => {
  const advancementsObj = {};
  for (const adv of advs) {
    const id = adv.id;
    if (!advancementsObj[id]) {
      advancementsObj[id] = adv;
      continue;
    }
    const statusCompareVal = advancementStatus.statusCompare(
      adv.status,
      advancementsObj[id].status,
    );
    if (
      statusCompareVal > 0 ||
      (statusCompareVal == 0 &&
        Number(adv.versionId) > Number(advancementsObj[id][adv.versionId]))
    ) {
      advancementsObj[id] = adv;
    }
  }

  return Object.values(advancementsObj);
};

/**
 * @esbEndpoint GET /advancements/v2/ranks/:advancementId/requirements
 * @esbEndpoint GET /advancements/meritBadges/:advancementId/requirements
 * @esbEndpoint GET /advancements/adventures/:advancementId/requirements
 * @esbEndpoint GET /advancements/awards/:advancementId/requirements
 * @esbEndpoint GET /advancements/ssElectives/:advancementId/requirements
 */
const fetchAdvRequirements$ = ({
  advancementType,
  advancementId,
  versionId,
  /** @type {import('@reduxjs/toolkit').ThunkDispatch} */
  dispatch,
  forceRefetch,
}) => {
  const uri =
    advancementType === advancementTypes.RANKS
      ? ADVANCEMENTS_URI
      : ADVANCEMENTS_URI_OLD;
  const versionIdParam =
    advancementType !== advancementTypes.SSELECTIVES
      ? 'versionId'
      : 'ssVersionId';
  const versionParam = versionId ? `?${versionIdParam}=${versionId}` : '';
  const endpoint = `${uri}/${advancementType}/${advancementId}/requirements${versionParam}`;

  if (
    featureFlags.getFlag('SBL_6604_ADVENTURES_RTK') &&
    advancementType === advancementTypes.ADVENTURES &&
    dispatch
  ) {
    const thunk = dispatch(
      esbApi.endpoints.getAdventureRequirements.initiate(
        { adventureId: advancementId, params: { versionId } },
        { forceRefetch },
      ),
    ).then(result => {
      if (result.error) throw result;
      return result.data;
    });

    return Observable.fromPromise(thunk);
  }

  if (
    featureFlags.getFlag('SBL_6604_ADVENTURES_RTK') &&
    advancementType === advancementTypes.RANKS &&
    dispatch
  ) {
    const thunk = dispatch(
      esbApi.endpoints.getRankRequirements.initiate(
        {
          rankId: advancementId,
          params: { versionId },
        },
        { forceRefetch },
      ),
    ).then(result => {
      if (result.error) throw result;
      return result.data;
    });

    return Observable.fromPromise(thunk);
  }

  return esbApiService.get$(endpoint, {
    gtm: {
      label: `${ADVANCEMENTS_URI}/${advancementType}/${advancementId}/requirements?versionId=${versionId}`,
    },
  });
};

/**
 * @esbEndpoint PUT /advancements/v2/:userId/:advancementType/:advancementId/version
 */
const updateActiveVersion$ = ({
  userId,
  advancementType,
  advancementId,
  versionId,
}) =>
  esbApiService.put$(
    `${ADVANCEMENTS_URI}/${userId}/${advancementType}/${advancementId}/version`,
    { versionId: versionId },
    {
      gtm: {
        label: `${ADVANCEMENTS_URI}/${userId}/${advancementType}/${advancementId}/version`,
      },
    },
  );

/**
 * @esbEndpoint GET /advancements/v2/:userId/:advancementType/:advancementId/requirements
 */
const fetchYouthAdvRequirements$ = ({
  userId,
  advancementType,
  advancementId,
  omitYouthRequirement,
  dispatch,
  forceRefetch,
}) => {
  const endpoint = `${ADVANCEMENTS_URI}/youth/${userId}/${advancementType}/${advancementId}/requirements`;
  const label = `${ADVANCEMENTS_URI}/youth/{userId}/${advancementType}/${advancementId}/requirements`;

  // Youth adv record might not exist if the youth has not started working on the advancement yet
  // so there's no need to retrieve if it not started can be identified
  if (omitYouthRequirement || endpoint.includes('undefined')) {
    return of({ status: NOT_STARTED });
  }

  if (
    featureFlags.getFlag('SBL_6604_ADVENTURES_RTK') &&
    advancementType === advancementTypes.ADVENTURES &&
    dispatch
  ) {
    const thunk = dispatch(
      esbApi.endpoints.getYouthAdventureRequirements.initiate(
        { adventureId: advancementId, userId: userId },
        { forceRefetch },
      ),
    ).then(result => {
      if (result.error) {
        if (result.error.status !== 404) {
          throw result;
        }
        return { status: NOT_STARTED };
      }
      return result.data;
    });

    return Observable.fromPromise(thunk);
  }

  if (
    featureFlags.getFlag('SBL_6604_ADVENTURES_RTK') &&
    advancementType === advancementTypes.RANKS &&
    dispatch
  ) {
    const thunk = dispatch(
      esbApi.endpoints.getYouthRankRequirements.initiate(
        { rankId: advancementId, userId: userId },
        { forceRefetch },
      ),
    ).then(result => {
      if (result.error) {
        if (result.error.status !== 404) {
          throw result;
        }
        return { status: NOT_STARTED };
      }
      return result.data;
    });

    return Observable.fromPromise(thunk);
  }

  // Ignore error 404 if requirement not existing
  return esbApiService
    .get$(endpoint, {
      suppressErrorToasts: [404],
      suppressSentry: [404],
      gtm: {
        label,
      },
    })
    .pipe(
      catchError(err => {
        if (err.status !== 404) {
          throw err;
        }
        return of({ status: NOT_STARTED });
      }),
    );
};

const mergeAdvRequirements = (
  advReqs = {},
  youthAdvReqs = {},
  advancementId,
) => {
  const { requirements: reqDict = [], rankInformation = {} } = advReqs;
  const { requirements: youthReqs = {} } = youthAdvReqs;
  const requirements = reqDict.map(req => {
    const reqId = req.id || req.requirementId || req.ssElectiveRequirementId;
    const youthReq = youthReqs.find(youthReq => +youthReq.id === +reqId) || {};
    const mergedReq = {
      ...req,
      ...youthReq,
      id: +reqId,
    };
    if (
      !isNil(req.linkedElectiveAdventures) &&
      !isEmpty(req.linkedElectiveAdventures)
    ) {
      mergedReq.linkedElectiveAdventures = uniqBy(
        [
          ...(youthReq.linkedElectiveAdventures || []),
          ...req.linkedElectiveAdventures,
        ],
        'id',
      );
    }
    if (req.requiresSSElective) {
      mergedReq.mergedSSElectives = uniqBy(
        [...(mergedReq.linkedSSElective || []), ...mergedReq.ssElectives],
        'ssElectiveId',
      );
    }
    return mergedReq;
  });

  return {
    ...rankInformation,
    ...advReqs,
    ...youthAdvReqs,
    id: advancementId,
    requirements,
  };
};

const mapAdvRequirementsDtoToModel = (dto, advancementType) => {
  const advStatus = advancementStatus.getStatus(dto);
  let requirements = getRequirementsTree(
    addMeritBadgeRequirementsStatus(dto.requirements),
  );
  if (advancementType === advancementTypes.MERIT_BADGES) {
    requirements = updateApprovedStatusFromChildren(requirements);
  }
  return {
    ...dto,
    advancementType,
    percentCompleted: Math.floor(Number(dto.percentCompleted) * 100),
    eagleRequired: isValueTruthy(dto.eagleRequired),
    status: advStatus,
    date: advancementStatusDate.getCompleted(dto),
    imgUrl: dto.imageUrl100,
    description: `${getAdvancementTypeTranslation(advancementType)}: ${
      dto.name
    }`,
    requirements,
  };
};

const getYouthAdvRequirements$ = ({
  userId,
  advancementType,
  advancementId,
  omitYouthRequirement,
  context = null,
  dispatch,
  forceRefetch,
}) =>
  // first fetch requirement for current youth
  fetchYouthAdvRequirements$({
    userId,
    advancementType,
    advancementId,
    omitYouthRequirement,
    dispatch,
    forceRefetch,
  }).pipe(
    switchMap(youthAdvReqs => {
      // then fetch full requirements based on active version for current youth
      const normalizedYouthAdvReqs = { ...youthAdvReqs };
      if (advancementType === advancementTypes.SSELECTIVES) {
        normalizedYouthAdvReqs.requirements = addSSElectiveRequirementsStatus(
          youthAdvReqs.requirements || [],
        );
      }
      return forkJoin([
        fetchAdvRequirements$({
          advancementType,
          advancementId,
          versionId:
            youthAdvReqs.versionId ||
            youthAdvReqs.ssVersionId ||
            youthAdvReqs.awardVersionId,
          dispatch,
          forceRefetch,
        }),
        of(normalizedYouthAdvReqs),
      ]);
    }),
    map(([advReqs, youthAdvReqs]) => {
      let youthReqsList = youthAdvReqs.requirements || [];
      const advancementInfo = {};
      if (advancementType === advancementTypes.RANKS) {
        advancementInfo.name = advReqs.rankInformation.name;
        advancementInfo.id = advReqs.rankInformation.rankId;
      }
      if (advancementType === advancementTypes.MERIT_BADGES) {
        advancementInfo.name = advReqs.meritBadgeName;
      }
      if (advancementType === advancementTypes.AWARDS) {
        advancementInfo.name = advReqs.awardName;
        advancementInfo.versionId = advReqs.awardVersionId;
      }
      if (advancementType === advancementTypes.SSELECTIVES) {
        advancementInfo.versionId =
          youthAdvReqs.ssVersionId || advReqs.ssVersionId;
        advancementInfo.version = youthAdvReqs.ssVersion || advReqs.ssVersion;
      }
      const transformedYouthReqs = {
        ...advancementInfo,
        ...youthAdvReqs,
        requirements: youthReqsList.map(youthReq => ({
          id:
            youthReq.id ||
            youthReq.requirementId ||
            youthReq.ssElectiveRequirementId,
          name:
            youthReq.name ||
            youthReq.requirementName ||
            youthReq.ssElectiveRequirementName,
          ...youthReq,
        })),
      };
      return mergeAdvRequirements(advReqs, transformedYouthReqs, advancementId);
    }),
    map(reqs => mapAdvRequirementsDtoToModel(reqs, advancementType)),
    catchError(error => {
      if (context === editAdvRqmtsRequest.type)
        throw new Error(GET_YOUTH_REQUIREMENTS_AFTER_EDIT_ERROR);
      else throw error;
    }),
  );

/**
 * @esbEndpoint POST /advancements/v2/youth/:advancementType/:advancementId/requirements
 */
const editAdvRequirements$ = ({
  userId,
  advancementType,
  advancementId,
  requirements,
  organizationGuid = null,
  context = null,
  dispatch,
  forceRefetch,
}) => {
  const endpoint = `${ADVANCEMENTS_URI}/youth/${advancementType}/${advancementId}/requirements`;
  const label = `${ADVANCEMENTS_URI}/youth/${advancementType}/{advancementId}/requirements`;
  const body = [
    {
      userId,
      requirements,
    },
  ];
  if (organizationGuid) {
    body[0].organizationGuid = organizationGuid;
  }

  return esbApiService
    .post$(endpoint, body, {
      gtm: {
        label,
      },
      suppressErrorToasts: true,
    })
    .pipe(
      switchMap(() =>
        getYouthAdvRequirements$({
          userId,
          advancementType,
          advancementId,
          context,
          dispatch,
          forceRefetch,
        }),
      ),
    );
};

const extractAdvancementsPayload = response => response || [];

/**
 * @esbEndpoint GET /advancements/v2/:userId/userActivitySummary
 */
const getPersonalActivitiesSummary$ = userId =>
  esbApiService
    .get$(`/advancements/v2/${userId}/userActivitySummary`, {
      gtm: {
        label: '/advancements/v2/{userId}/userActivitySummary',
      },
      suppressErrorToasts: [401],
    })
    .pipe(
      catchError(err => {
        if (err.status !== 401) {
          throw err;
        }
        return Observable.of({});
      }),
    );

const createEagleActivity$ = data =>
  esbApiService.post$(
    '/advancements/v2/activities/add',
    {
      ...data,
    },
    {
      gtm: {
        label: '/advancements/v2/activities/add',
      },
    },
  );

/**
 * @esbEndpoint PUT /advancements/v2/activities/:activityId
 */
const updateEagleActivity$ = (id, data) =>
  esbApiService.put$(`/advancements/v2/activities/${id}`, data, {
    gtm: {
      label: '/advancements/activities/{id}',
    },
  });

/**
 * @esbEndpoint PUT /advancements/activities/:activityId/registeredParticipants
 */
const updateEagleParticipants$ = (activityId, payload) =>
  esbApiService.put$(
    `/advancements/activities/${activityId}/registeredParticipants`,
    payload,
    {
      gtm: {
        label: '/advancements/activities/{activityId}/registeredParticipants',
      },
    },
  );

/**
 * @esbEndpoint GET /advancements/activities/:id
 */
const getEagleActivity$ = id =>
  esbApiService.get$(`/advancements/activities/${id}`, {
    gtm: {
      label: '/advancements/activities/{id}',
    },
  });

/**
 * @esbEndpoint POST /persons/:personGuid/subscriptions
 */
const createUsersubscriptions$ = (data, personGuid) =>
  esbApiService.post$(`/persons/${personGuid}/subscriptions`, data, {
    gtm: {
      label: '/persons/{personGuid}/subscriptions',
    },
  });

/**
 * @esbEndpoint PUT /persons/:personGuid/subscriptions/:id
 */
const updateUsersubscriptions$ = (data, personGuid) =>
  esbApiService.put$(`/persons/${personGuid}/subscriptions/${data.id}`, data, {
    gtm: {
      label: '/persons/{personGuid}/subscriptions/{id}',
    },
  });

/**
 * @esbEndpoint POST /advancements/:youthUserId/pictureUpload
 */
const getPreSignedUrl$ = (userId, imgType) =>
  esbApiService.post$(
    `/advancements/${userId}/pictureUpload`,
    {
      contentType: imgType,
    },
    {
      gtm: {
        label: '/advancements/{userId}/pictureUpload',
      },
    },
  );

/**
 * @esbEndpoint GET /advancements/v2/youth/:userId/ranks
 */
const getYouthRanks$ = userId =>
  esbApiService
    .get$(`${ADVANCEMENTS_URI}/youth/${userId}/ranks`, {
      gtm: {
        label: `${ADVANCEMENTS_URI}/youth/{userId}/ranks`,
      },
      suppressErrorToasts: [401],
    })
    .pipe(
      map(({ program }) => {
        const allYouthRanks = program.flatMap(({ ranks = [] }) => ranks);
        const ranks = dedupeAdvancements(
          allYouthRanks.map(rank => {
            const advancementType = advancementTypes.RANKS;
            return {
              ...rank,
              percentCompleted: Math.floor(Number(rank.percentCompleted) * 100),
              advancementType,
              userId,
              uid: getAdvancementUid(rank, advancementType),
              status: advancementStatus.getStatus({ ...rank, advancementType }),
              date: advancementStatusDate.getCompleted(rank),
            };
          }),
        );

        const sortedRanks = stable(
          ranks,
          (a, b) => Number(a.level) > Number(b.level),
        );
        return sortedRanks;
      }),
      catchError(err => {
        if (err.status !== 401) {
          throw err;
        }
        return of([]);
      }),
    );

/**
 * @esbEndpoint GET /advancements/v2/youth/:userId/adventures
 */
const getYouthAdventures$ = userId =>
  esbApiService
    .get$(`${ADVANCEMENTS_URI}/youth/${userId}/adventures`, {
      gtm: {
        label: `${ADVANCEMENTS_URI}/youth/{userId}/adventures`,
      },
      suppressErrorToasts: [401],
    })
    .pipe(
      map(response => {
        const userAdventures = extractAdvancementsPayload(response);

        return dedupeAdvancements(
          userAdventures.map(adventure => ({
            // API v2 mappings 🤷🏻‍♂️
            name: adventure.adventureName,
            id: adventure.adventureId,
            ...adventure,
            percentCompleted: Math.floor(
              Number(adventure.percentCompleted) * 100,
            ),
            advancementType: advancementTypes.ADVENTURES,
            uid: getAdvancementUid(adventure, advancementTypes.ADVENTURES),
            status: advancementStatus.getStatus(adventure),
            date: advancementStatusDate.getCompleted(adventure),
          })),
        );
      }),
      catchError(err => {
        if (err.status !== 401) {
          throw err;
        }
        return of([]);
      }),
    );

/**
 * @esbEndpoint GET /advancements/v2/youth/:userId/awards
 */
const getYouthAwards$ = userId =>
  esbApiService
    .get$(`${ADVANCEMENTS_URI}/youth/${userId}/awards`, {
      gtm: {
        label: `${ADVANCEMENTS_URI}/youth/{userId}/awards`,
      },
      suppressErrorToasts: [401],
    })
    .pipe(
      map(response => {
        const userAwards = extractAdvancementsPayload(response);

        return userAwards.map(award => ({
          ...award,
          userId,
          percentCompleted: Math.floor(Number(award.percentCompleted) * 100),
          advancementType: advancementTypes.AWARDS,
          id: award.awardId,
          userAwardId: Number(award.userAwardId),
          uid: getAdvancementUid(award, advancementTypes.AWARDS),
          status: advancementStatus.getStatus(award),
          date: advancementStatusDate.getCompleted(award),
          programId: award.programId || award.unitTypeId,
          level: venturingCoreAwardsIds.includes(`${award.awardId}`)
            ? award.awardId
            : undefined,
        }));
      }),
      catchError(err => {
        if (err.status !== 401) {
          throw err;
        }
        return of([]);
      }),
    );

/**
 * @esbEndpoint GET /advancements/v2/youth/:userId/meritBadges
 */
const getYouthMeritBadges$ = userId =>
  esbApiService
    .get$(`${ADVANCEMENTS_URI}/youth/${userId}/meritBadges`, {
      gtm: {
        label: `${ADVANCEMENTS_URI}/youth/{userId}/meritBadges`,
      },
      suppressErrorToasts: [401],
    })
    .pipe(
      map(response => {
        const meritBadges = extractAdvancementsPayload(response);

        return meritBadges.map(meritBadge => ({
          ...meritBadge,
          userId,
          percentCompleted: Math.floor(
            Number(meritBadge.percentCompleted) * 100,
          ),
          isEagleScoutRequired: meritBadge.eagleRequired == 'True',
          advancementType: advancementTypes.MERIT_BADGES,
          uid: getAdvancementUid(meritBadge, advancementTypes.MERIT_BADGES),
          status: advancementStatus.getStatus(meritBadge),
          date: advancementStatusDate.getCompleted(meritBadge),
        }));
      }),
      catchError(err => {
        if (err.status !== 401) {
          throw err;
        }
        return of([]);
      }),
    );

/**
 * @esbEndpoint GET /advancements/youth/:userId/ssElectives
 */
const getYouthSSElectives$ = userId =>
  esbApiService
    .get$(`${ADVANCEMENTS_URI_OLD}/youth/${userId}/ssElectives`, {
      gtm: {
        label: `${ADVANCEMENTS_URI_OLD}/youth/{userId}/ssElectives`,
      },
      suppressErrorToasts: [401],
    })
    .pipe(
      map(response => {
        const ssElectives = extractAdvancementsPayload(response);
        const groupedSSElectives = ssElectives.reduce((acc, ssElective) => {
          if (!isNil(ssElective.childrenRequired)) {
            const children = ssElectives.filter(
              child =>
                child.parentRequirementId ===
                ssElective.ssElectiveRequirementId,
            );
            acc.push({
              ...ssElective,
              requirements: children,
            });
          } else if (isNil(ssElective.parentRequirementId)) {
            acc.push({ ...ssElective });
          }
          return acc;
        }, []);
        return groupedSSElectives.map(ssElective => ({
          ...ssElective,
          id: ssElective.ssElectiveId,
          name: ssElective.ssElectiveName,
          level: ssElective.ssElectiveLevel,
          userId,
          advancementType: advancementTypes.SSELECTIVES,
          uid: getAdvancementUid(ssElective, advancementTypes.SSELECTIVES),
          status: advancementStatus.getStatus(ssElective),
          date: advancementStatusDate.getCompleted(ssElective),
        }));
      }),
      catchError(err => {
        if (err.status !== 401) {
          throw err;
        }
        return of([]);
      }),
    );

const getYouthAdvancements$ = ({
  eligibleAdvancementTypes,
  userId,
  unitTypeId,
}) => {
  const allEligibleAdvancementTypes = getEligibleAdvancementTypes(
    eligibleAdvancementTypes,
    unitTypeId,
  );
  const getAdvancements$ = fallBackFactory({
    eligibleAdvancementTypes: allEligibleAdvancementTypes,
    userId,
  });

  return forkJoin(
    getAdvancements$(getYouthAdventures$, 'adventures'),
    getAdvancements$(getYouthAwards$, 'awards'),
    getAdvancements$(getYouthMeritBadges$, 'meritBadges'),
    getAdvancements$(getYouthRanks$, 'ranks'),
    getAdvancements$(getYouthSSElectives$, 'ssElectives'),
  ).pipe(
    map(([adventures, awards, meritBadges, ranks, ssElectives]) => ({
      userId,
      adventures,
      awards,
      meritBadges,
      ranks,
      ssElectives,
    })),
  );
};

const getYouthData$ = ({
  userId,
  eligibleAdvancementTypes,
  unitTypeId,
  isParentOrYouthMember,
}) =>
  forkJoin(
    getYouthProfile$(userId),
    getYouthAdvancements$({
      eligibleAdvancementTypes,
      userId,
      unitTypeId,
    }),
  ).pipe(
    switchMap(([youthProfile, youthAdvancements]) => {
      const forkJoinArray = [of(youthProfile), of(youthAdvancements)];
      return forkJoin(forkJoinArray);
    }),
    map(([youthProfile, youthAdvancements, relationships = []]) => {
      const { profile: { memberId } = {} } = youthProfile;
      // Report to sentry to debug if memberId is missing or some youths
      if (isParentOrYouthMember && !memberId) {
        sentryReport.info({
          message: `Missing member id ${userId}`,
          responseData: youthProfile,
        });
      }

      return {
        ...youthProfile,
        ...youthAdvancements,
        isParentOrYouthMember,
        relationships: filterRelationships(relationships),
      };
    }),
  );

export default {
  getYouthData$,
  getYouthAdvancements$,
  getYouthAdvRequirements$,
  editAdvRequirements$,
  getPersonalActivitiesSummary$,
  saveProfilePicture$,
  getYouthProfile$,
  getRelationships$,
  saveProfile$,
  updateSBProfile$,
  deleteProfileItem$,
  createEagleActivity$,
  updateEagleActivity$,
  updateEagleParticipants$,
  getEagleActivity$,
  createUsersubscriptions$,
  updateUsersubscriptions$,
  getPreSignedUrl$,
  updateActiveVersion$,
  startYouthMeritBadge$,
};
